import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { fmtIDR, fmtDateTime } from "../lib/format";
import { PageHeader, Card, Badge, Modal, Btn, inputCls, statusTone } from "../components/common";
import SignaturePad from "../components/SignaturePad";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, XCircle, Clock, Download } from "lucide-react";
import { toast } from "sonner";

export default function Approvals() {
  const { user } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [rules, setRules] = useState([]);
  const [action, setAction] = useState(null);
  const [apDetail, setApDetail] = useState(null);
  const openDetail = async (a) => {
    try {
      const r = await api.get(`/approvals/${a.id}/detail`);
      setApDetail(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [signature, setSignature] = useState(null);
  const [note, setNote] = useState("");
  const [signTab, setSignTab] = useState("pad");
  const [specimenUrl, setSpecimenUrl] = useState(null);

  const fetchSpecimen = async () => {
    try {
      const r = await api.get("/users/me/signature", { responseType: "blob" });
      setSpecimenUrl(URL.createObjectURL(r.data));
    } catch { setSpecimenUrl(null); }
  };

  const uploadSpecimen = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/users/me/signature", { signature: reader.result });
        toast.success("Specimen tanda tangan tersimpan");
        fetchSpecimen();
      } catch (err) { toast.error(errMsg(err)); }
    };
    reader.readAsDataURL(file);
  };

  const useSpecimen = async () => {
    try {
      const r = await api.get("/users/me/signature", { responseType: "blob" });
      const reader = new FileReader();
      reader.onload = () => { setSignature(reader.result); toast.success("Specimen tanda tangan dipakai — dengan timestamp"); };
      reader.readAsDataURL(r.data);
    } catch { toast.error("Belum ada specimen tersimpan"); }
  };
  const [tab, setTab] = useState("pending");

  const load = useCallback(() => {
    api.get("/approvals").then((r) => setApprovals(r.data));
    api.get("/approvals/rules").then((r) => setRules(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (act) => {
    if (act === "approve" && !signature) return toast.error("Tanda tangan digital wajib diisi");
    try {
      await api.post(`/approvals/${action.id}/action`, { action: act, signature: signature || "", note });
      toast.success(act === "approve" ? "Disetujui & ditandatangani" : "Ditolak");
      setAction(null); setSignature(null); setNote(""); setSignTab("pad"); setSpecimenUrl(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = approvals.filter((a) => tab === "pending" ? a.status === "pending" : a.status !== "pending");

  return (
    <div data-testid="approvals-page">
      <PageHeader title="Approve & Sign" subtitle="Workflow persetujuan bertingkat dengan tanda tangan digital & timestamp" testid="approvals-header" />

      <div className="flex gap-2 mb-5 flex-wrap">
        {[["pending", "Menunggu"], ["history", "Riwayat"], ["rules", "Aturan Workflow"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`approval-tab-${k}`}
            className={`text-sm px-4 py-2 rounded-lg border transition-colors ${tab === k ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900 hover:text-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab !== "rules" && (
        <div className="space-y-3" data-testid="approval-list">
          {filtered.length === 0 && <Card><p className="text-slate-500 text-sm">Tidak ada data.</p></Card>}
          {filtered.map((a) => (
            <Card key={a.id} testid={`approval-card-${a.id}`} className="!p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge tone="cyan">{a.doc_type.replace("ACT:", "Aksi: ")}</Badge>
                    <span className="text-sm font-medium text-slate-100">{a.title}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Diminta oleh {a.requested_by} &middot; {fmtDateTime(a.created_at)}</div>
                  {a.amount > 0 && <div className="font-mono text-sm text-emerald-400 mt-1">{fmtIDR(a.amount)}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <Btn tone="ghost" className="!text-xs" onClick={() => openDetail(a)} data-testid={`approval-detail-${a.id}`}>Lihat Detail</Btn>
                  <Badge tone={statusTone(a.status)}>{a.status}</Badge>
                  {a.needs_my_action && a.status === "pending" && (
                    <Btn className="!text-xs" onClick={() => { setAction(a); setSignature(null); setNote(""); }} data-testid={`approval-open-${a.id}`}>Proses</Btn>
                  )}
                  {a.doc_type === "SPK_PROYEK" && a.status === "approved" && (
                    <Btn tone="ghost" className="!text-xs" onClick={async () => {
                      try {
                        const r = await api.get(`/approvals/${a.id}/document`);
                        const fmt = r.data.has_pdf ? "pdf" : "docx";
                        const res = await api.get(`/documents/generated/${r.data.id}/file?fmt=${fmt}`, { responseType: "blob" });
                        const url = URL.createObjectURL(res.data);
                        const aEl = document.createElement("a");
                        aEl.href = url;
                        aEl.download = `${r.data.number}.${fmt}`;
                        aEl.click();
                        URL.revokeObjectURL(url);
                        toast.success("SPK final bertanda tangan diunduh");
                      } catch (e) { toast.error(errMsg(e)); }
                    }} data-testid={`spk-download-${a.id}`}><Download className="w-3.5 h-3.5" /> SPK Final</Btn>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {a.levels.map((l) => (
                  <div key={l.level} className={`flex items-center gap-2 text-xs border rounded-lg px-3 py-2 ${l.status === "approved" ? "border-emerald-500/40 bg-emerald-500/5" : l.status === "rejected" ? "border-rose-500/40 bg-rose-500/5" : l.level === a.current_level ? "border-amber-500/40 bg-amber-500/5" : "border-slate-800"}`} data-testid={`approval-level-${a.id}-${l.level}`}>
                    {l.status === "approved" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : l.status === "rejected" ? <XCircle className="w-3.5 h-3.5 text-rose-400" /> : <Clock className="w-3.5 h-3.5 text-amber-400" />}
                    <div>
                      <div className="text-slate-300">Lv{l.level}: {l.role}</div>
                      {l.signed_by && (
                        <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                          {l.signed_by} &middot; {fmtDateTime(l.at)}
                          {l.signature && <img src={l.signature} alt="ttd" className="h-4 bg-slate-800 rounded" data-testid={`approval-sig-${a.id}-${l.level}`} />}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === "rules" && (
        <div className="overflow-x-auto rounded-xl border border-slate-800" data-testid="rules-table">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-900 border-b border-slate-800">
              {["Tipe Dokumen", "Nominal Minimum", "Level", "Approver"].map((h) => <th key={h} className="text-left text-xs font-mono uppercase tracking-wider text-slate-500 px-4 py-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-b border-slate-800/60" data-testid={`rule-row-${r.id}`}>
                  <td className="px-4 py-3"><Badge tone="cyan">{r.doc_type}</Badge></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-300">≥ {fmtIDR(r.min_amount)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-300">Level {r.level}</td>
                  <td className="px-4 py-3 text-slate-300">{r.approver_role}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-500 p-4">Aturan dikelola di menu Pengaturan & RBAC (Super Admin).</p>
        </div>
      )}

      <Modal open={!!action} onClose={() => setAction(null)} title={action ? `Proses: ${action.title}` : ""} testid="approval-action-modal">
        {action && (
          <div className="space-y-4">
            <div className="text-sm text-slate-400">Anda memproses sebagai <span className="text-emerald-400 font-medium">{user?.role}</span> pada Level {action.current_level}.</div>
            {action.amount > 0 && <div className="font-mono text-lg text-slate-100">{fmtIDR(action.amount)}</div>}
            <div>
              <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">Catatan</span>
              <textarea className={inputCls} rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="approval-note-input" />
            </div>
            <div>
              <div className="flex gap-2 mb-2">
                <button onClick={() => setSignTab("pad")} data-testid="sig-tab-pad"
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${signTab === "pad" ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10" : "border-slate-700 text-slate-400 hover:text-white"}`}>
                  Tanda Tangan di Layar
                </button>
                <button onClick={() => { setSignTab("specimen"); fetchSpecimen(); }} data-testid="sig-tab-specimen"
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${signTab === "specimen" ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10" : "border-slate-700 text-slate-400 hover:text-white"}`}>
                  Specimen Tersimpan
                </button>
              </div>
              {signTab === "pad" ? (
                <SignaturePad onSave={(sig) => { setSignature(sig); toast.success("Tanda tangan tersimpan dengan timestamp"); }} />
              ) : (
                <div className="space-y-2" data-testid="specimen-section">
                  {specimenUrl ? (
                    <div className="bg-white rounded-lg border border-slate-700 p-3 flex flex-col items-center gap-2">
                      <img src={specimenUrl} alt="specimen tanda tangan" className="max-h-24" data-testid="specimen-preview" />
                      <Btn tone="ghost" className="!text-xs" onClick={useSpecimen} data-testid="specimen-use-button"><CheckCircle2 className="w-3.5 h-3.5" /> Gunakan Specimen Ini</Btn>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500" data-testid="specimen-empty">Belum ada specimen tersimpan.</p>
                  )}
                  <label className="w-full border-2 border-dashed border-slate-700 hover:border-emerald-500/50 rounded-lg py-4 text-slate-500 hover:text-emerald-300 transition-colors text-xs block text-center cursor-pointer" data-testid="specimen-upload-button">
                    {specimenUrl ? "Ganti specimen (PNG transparan)" : "Unggah specimen tanda tangan (PNG transparan)"}
                    <input type="file" accept="image/png" onChange={uploadSpecimen} className="hidden" data-testid="specimen-upload-input" />
                  </label>
                </div>
              )}
            </div>
            {signature && <div className="text-xs text-emerald-400 flex items-center gap-1.5" data-testid="signature-saved-indicator"><CheckCircle2 className="w-3.5 h-3.5" /> Tanda tangan siap — {new Date().toLocaleString("id-ID")}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Btn tone="danger" onClick={() => submit("reject")} data-testid="reject-approval-button"><XCircle className="w-4 h-4" /> Tolak</Btn>
              <Btn onClick={() => submit("approve")} disabled={!signature} data-testid="submit-approval-button"><CheckCircle2 className="w-4 h-4" /> Setujui & Tanda Tangan</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!apDetail} onClose={() => setApDetail(null)} title={apDetail ? `Detail: ${apDetail.title}` : ""} wide testid="approval-detail-modal">
        {apDetail && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              {apDetail.fields.map(([k, v], i) => (
                <div key={i} className="flex justify-between gap-4 text-sm border-b border-slate-800/60 py-1.5">
                  <span className="text-slate-500 text-xs uppercase tracking-wide">{k}</span>
                  <span className="text-slate-200 text-right font-mono text-xs">{typeof v === "number" ? new Intl.NumberFormat("id-ID").format(v) : String(v ?? "-")}</span>
                </div>
              ))}
            </div>
            {apDetail.items.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-slate-800">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-900 border-b border-slate-800">
                      {Object.keys(apDetail.items[0]).map((k) => <th key={k} className="text-left font-mono uppercase tracking-wider text-slate-500 px-3 py-2">{k}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {apDetail.items.map((it, i) => (
                      <tr key={i} className="border-b border-slate-800/60">
                        {Object.values(it).map((v, vi) => <td key={vi} className="px-3 py-2 text-slate-300 font-mono">{String(v ?? "-")}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
